/**
 * Souvenirs — the entire cosmetics system of the game.
 *
 * One souvenir per cleared island (GDD §2.1), declared in content/mapa.json on the island that
 * grants it, never bought and never picked from a menu (GDD §10.1). You wear what you earned,
 * automatically, everywhere the character is drawn — the map is the wardrobe. "Imaš tri stvari
 * zato što si prešao tri ostrva. Ne treba objašnjenje."
 *
 * Both of the ways a cosmetic could be earned are already inside "island cleared": the day lock
 * is the time component, and the big task — which only counts once it is *revised* — is the
 * specific piece of work. A souvenir is therefore a revision made visible, which is the one
 * behaviour this product exists to produce. Nothing here rewards mere calendar time; time alone
 * never opens doors in this game, and it does not dress anyone either.
 *
 * Art contract: `wear` is a map of body-row index → 16-char row painted over the composed hero
 * with '.' meaning "keep what is underneath". Rows are in body space (1-13). Row 0 is forbidden:
 * the walk bob drops the body's empty top row, and anything drawn there would be clipped off on
 * every other step. A test enforces this.
 *
 * Adding the next island's souvenir is: rows here, a name in copy.ts, the id in mapa.json.
 * An id without art draws nothing — content can ship ahead of pixels without breaking anyone.
 */

/** Painted over the hero. `side` serves left and right — left is a mirror, like the body. */
export interface SouvenirArt {
  wear: Record<'down' | 'up' | 'side', Record<number, string>>;
  /** Standalone item sprite: the island screen today, the LIK screen in Phase 3. */
  icon: readonly string[];
}

export const SOUVENIRS: Record<string, SouvenirArt> = {
  /**
   * TEMELJ. A snapback in the Talas cyan with a white wave on the front. The first island that
   * asks for a real posted work hands you the crew's cap.
   */
  kapa: {
    wear: {
      down: {
        // Crown over the hair; the head's own black outline (row 1) becomes the cap's outline.
        2: '.....aaaaaa.....',
        3: '...aaaawwaaaa...',
        // The brim shades the brow but leaves a pixel of hair at each temple, so the head still
        // reads as a head with a cap on it rather than as a repainted scalp.
        4: '....AAAAAAAA....',
      },
      up: {
        2: '.....aaaaaa.....',
        3: '...aaaaaaaaaa...',
        // From behind, the dark row is the strap; the hair stays visible under it.
        4: '...AAAAAAAAAA...',
      },
      side: {
        2: '.....aaaaa......',
        3: '....aaaawwa.....',
        // The brim juts one pixel past the face, with its own outline pixel on the tip.
        4: '....AAAAAAAAAk..',
      },
    },
    icon: [
      '................',
      '................',
      '................',
      '.....kkkkkk.....',
      '....kaaaaaak....',
      '...kaaaaaaaak...',
      '...kaaaawwaak...',
      '...kaaaaaaaak...',
      // The brim goes one way, like on the head. Both ways is a different hat.
      '...kAAAAAAAAAAk.',
      '....kkkkkkkkkk..',
      '................',
      '................',
      '................',
      '................',
      '................',
      '................',
    ],
  },

  /**
   * REZ. Gold headphones worn around the neck — the working editor's resting position, which is
   * also why they never fight the kapa for space. REZ is the island whose spine is sound design;
   * you leave it with the tool of that trade on your collar.
   */
  slusalice: {
    wear: {
      down: {
        // The band covers the base of the neck; two cups hang on the chest either side of it.
        8: '.....kkkkkk.....',
        9: '.....yy..yy.....',
        10: '.....YY..YY.....',
      },
      up: {
        8: '.....kkkkkk.....',
        // Just the cup tops peeking over the shoulders.
        9: '.....y....y.....',
      },
      side: {
        8: '......kkkk......',
        // One cup visible on the near side, same depth rule as the legs.
        9: '.......kyy......',
        10: '.......kYY......',
      },
    },
    icon: [
      '................',
      '.....kkkkkk.....',
      '....k......k....',
      '...k........k...',
      '...k........k...',
      '...k........k...',
      '..kk........kk..',
      '.kyyk......kyyk.',
      '.kyyk......kyyk.',
      '.kYYk......kYYk.',
      '..kk........kk..',
      '................',
      '................',
      '................',
      '................',
      '................',
    ],
  },
};

/** Ids that actually have art, in the order given — the order they were earned in. */
export function wearable(ids: readonly string[]): string[] {
  return ids.filter((id) => SOUVENIRS[id]);
}
